'use client';

import { useCallback, useState, type ChangeEvent, type FormEvent } from 'react';
import { useTranslations } from 'next-intl';
import { FileSearch, Search, XCircle } from 'lucide-react';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { useRouter } from '@/i18n/request';
import { VerificationShell, VerificationStateCard } from './_components/verification-shell';
import { parseDocumentId } from './_utils/parse-document-id';

const DocumentsVerificationSearchPage = () => {
  const t = useTranslations('Documents.verification');
  const router = useRouter();
  const [value, setValue] = useState('');
  const [hasError, setHasError] = useState(false);

  const handleSubmit = useCallback(
    (event: FormEvent<HTMLFormElement>) => {
      event.preventDefault();

      const documentId = parseDocumentId(value);

      if (!documentId) {
        setHasError(true);
        return;
      }

      setHasError(false);
      router.push(`/documents/${documentId}`);
    },
    [router, value]
  );

  const handleChange = useCallback((event: ChangeEvent<HTMLInputElement>) => {
    setValue(event.target.value);
    setHasError(false);
  }, []);

  return (
    <VerificationShell>
      <VerificationStateCard>
        <div className="mx-auto max-w-xl space-y-6">
          <div className="space-y-3 text-center">
            <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-[var(--radius-control)] border border-primary/15 bg-primary/10 text-primary">
              <FileSearch className="h-6 w-6" />
            </div>
            <h1 className="text-2xl font-semibold tracking-tight text-foreground sm:text-3xl">{t('search.title')}</h1>
            <p className="text-sm text-muted-foreground">{t('search.description')}</p>
          </div>

          <form className="space-y-4" onSubmit={handleSubmit} noValidate>
            <div className="space-y-2">
              <Label htmlFor="document-id">{t('search.label')}</Label>
              <Input
                id="document-id"
                name="document-id"
                value={value}
                onChange={handleChange}
                placeholder={t('search.placeholder')}
                autoComplete="off"
                autoCapitalize="none"
                spellCheck={false}
                aria-invalid={hasError}
                aria-describedby={hasError ? 'document-id-error' : undefined}
              />
              <p className="text-xs text-muted-foreground">{t('search.hint')}</p>
            </div>

            {hasError ? (
              <Alert variant="destructive" id="document-id-error">
                <XCircle className="h-4 w-4" />
                <AlertDescription>{t('search.invalid_id')}</AlertDescription>
              </Alert>
            ) : null}

            <Button type="submit" className="w-full">
              <Search className="mr-2 h-4 w-4" />
              {t('search.submit')}
            </Button>
          </form>
        </div>
      </VerificationStateCard>
    </VerificationShell>
  );
};

export default DocumentsVerificationSearchPage;
